/**
 * User Milestones utilities.
 *
 * Milestones are achievements unlocked by users through marketplace activity.
 * Stored in user.protectedData.milestones as an array of milestone IDs.
 *
 * Built-in milestones:
 *   first_listing      — Created first listing
 *   first_booking      — Completed first booking (as customer)
 *   first_sale         — Completed first sale (as provider)
 *   five_reviews       — Received 5 reviews
 *   ten_reviews        — Received 10 reviews
 *   power_seller       — Completed 50 transactions as provider
 *   trusted_buyer      — Completed 20 transactions as customer
 *   verified_profile   — Added profile photo + bio
 *   early_adopter      — Joined in the first month of the marketplace
 */

export const MILESTONES = {
  first_listing: {
    id: 'first_listing',
    labelKey: 'UserMilestones.firstListing',
    icon: '🏠',
    description: 'Created first listing',
  },
  first_booking: {
    id: 'first_booking',
    labelKey: 'UserMilestones.firstBooking',
    icon: '🎉',
    description: 'Completed first booking',
  },
  first_sale: {
    id: 'first_sale',
    labelKey: 'UserMilestones.firstSale',
    icon: '💰',
    description: 'Completed first sale',
  },
  five_reviews: {
    id: 'five_reviews',
    labelKey: 'UserMilestones.fiveReviews',
    icon: '⭐',
    description: 'Received 5 reviews',
  },
  ten_reviews: {
    id: 'ten_reviews',
    labelKey: 'UserMilestones.tenReviews',
    icon: '🌟',
    description: 'Received 10 reviews',
  },
  power_seller: {
    id: 'power_seller',
    labelKey: 'UserMilestones.powerSeller',
    icon: '🚀',
    description: 'Completed 50 sales',
  },
  trusted_buyer: {
    id: 'trusted_buyer',
    labelKey: 'UserMilestones.trustedBuyer',
    icon: '🛡️',
    description: 'Completed 20 bookings',
  },
  verified_profile: {
    id: 'verified_profile',
    labelKey: 'UserMilestones.verifiedProfile',
    icon: '✅',
    description: 'Completed profile',
  },
  early_adopter: {
    id: 'early_adopter',
    labelKey: 'UserMilestones.earlyAdopter',
    icon: '🌱',
    description: 'Early adopter',
  },
};

/**
 * Get milestone definitions for a user's earned milestone IDs.
 * @param {string[]} earnedIds
 * @returns {Object[]} — array of milestone definition objects
 */
export const getEarnedMilestones = (earnedIds = []) =>
  earnedIds
    .filter(id => MILESTONES[id])
    .map(id => MILESTONES[id]);

/**
 * Check if a user has earned a specific milestone.
 * @param {string[]} earnedIds
 * @param {string} milestoneId
 * @returns {boolean}
 */
export const hasMilestone = (earnedIds = [], milestoneId) =>
  earnedIds.includes(milestoneId);

/**
 * Add a milestone to a user's list (deduplicates).
 * @param {string[]} current
 * @param {string} milestoneId
 * @returns {string[]}
 */
export const addMilestone = (current = [], milestoneId) => {
  if (!MILESTONES[milestoneId]) throw new Error(`Unknown milestone: ${milestoneId}`);
  if (current.includes(milestoneId)) return current;
  return [...current, milestoneId];
};
